import {
  ISyncChangesResponse,
  apiService,
  extractSyncResponse,
  teacherTrainingDB,
  SyncRecordStatus,
  bulkImageDownloadFromUrlToBase64,
  ICourse,
  ITraining,
  ProgressStatus,
  ITrainingQuestion,
  getModelCount,
  IQuestion,
  IGrandQuizzes,
  ILevels,
} from '..';

interface IGetCoursesResponse {
  timestamp: string;
  courses: ISyncChangesResponse<ICourse>;
  trainings: ISyncChangesResponse<ITraining>;
  profileType: string;
  grandQuizzes: ISyncChangesResponse<IGrandQuizzes>; // Add this
  levels: ISyncChangesResponse<ILevels>;
}
interface IGetProfileBasedCoursesResponse {
  profiles: {
    [profileId: string]: IGetCoursesResponse;
  };
}

interface IGetQuestionsReponse {
  timestamp: string;
  questions: ISyncChangesResponse<ITrainingQuestion>;
}

export interface IOpenEndedFeedbackResponse {
  score: number;
  feedback: string;
}

interface IGetGrandQuizResponse {
  timestamp: string;
  questions: ISyncChangesResponse<ITrainingQuestion>;
}

// Cold-install boot-time OOM mitigation: the sync response carries every
// training's full `content` HTML body (multi-MB each). At PAGE_SIZE=300 a
// single response peaks at hundreds of MB after JSON.parse + axios-case-
// converter deep-clone + getDataAgainstProfileIds spreads, crashing low-end
// Android (Crashlytics: 269 MB allocation on a 256 MB-capped WebView). 50
// keeps each page allocation bounded; the trade-off is 6x HTTP round-trips
// on cold install.
const PAGE_SIZE = 50;

const getDataAgainstProfileIds = (data: IGetProfileBasedCoursesResponse) => {
  const profileIds = Object.keys(data.profiles);
  const profileData = profileIds.map((profileId) => {
    const profile = data.profiles[profileId];
    const profileType = profile.profileType;
    // TODO: Normalizing profileId is not a good approach, we should resolve this in middleware function 'applyCaseMiddleware'
    const normalizedProfileId = profileId.replace(/_/g, '-').toLowerCase();

    const coursesWithProfile = {
      created: {
        ...profile.courses.created,
        data: profile.courses.created.data.map((course) => ({
          ...course,
          profileId: normalizedProfileId,
          profileType,
        })),
      },
      updated: {
        ...profile.courses.updated,
        data: profile.courses.updated.data.map((course) => ({
          ...course,
          profileId: normalizedProfileId,
          profileType,
        })),
      },
      deleted: profile.courses.deleted,
    };

    const trainingsWithProfile = {
      created: {
        ...profile.trainings.created,
        data: profile.trainings.created.data.map((training) => {
          // Strip `content` (full HTML, multi-KB each) — never read by any UI
          // component. 178 trainings × large HTML kept alive across recursive
          // fetchCourseData pages causes sync-time OOM on large schools.
          const { content: _drop, ...rest } = training as ITraining & { content?: string };
          return { ...rest, profileId: normalizedProfileId, profileType };
        }),
      },
      updated: {
        ...profile.trainings.updated,
        data: profile.trainings.updated.data.map((training) => {
          const { content: _drop, ...rest } = training as ITraining & { content?: string };
          return { ...rest, profileId: normalizedProfileId, profileType };
        }),
      },
      deleted: profile.trainings.deleted,
    };

    const grandQuizzesWithProfile = {
      created: {
        ...profile.grandQuizzes.created,
        data: profile.grandQuizzes.created.data.map((quiz) => ({
          ...quiz,
          profileId: normalizedProfileId,
          profileType,
        })),
      },
      updated: {
        ...profile.grandQuizzes.updated,
        data: profile.grandQuizzes.updated.data.map((quiz) => ({
          ...quiz,
          profileId: normalizedProfileId,
          profileType,
        })),
      },
      deleted: profile.grandQuizzes.deleted,
    };

    const levelsWithProfile = {
      created: {
        ...profile.levels.created,
        data: profile.levels.created.data.map((level) => ({
          ...level,
          profileId: normalizedProfileId,
          profileType,
        })),
      },
      updated: {
        ...profile.levels.updated,
        data: profile.levels.updated.data.map((level) => ({
          ...level,
          profileId: normalizedProfileId,
          profileType,
        })),
      },
      deleted: profile.levels.deleted,
    };

    return {
      profileId: normalizedProfileId,
      profileType,
      courses: coursesWithProfile,
      trainings: trainingsWithProfile,
      grandQuizzes: grandQuizzesWithProfile,
      levels: levelsWithProfile,
    };
  });
  return profileData;
};

export const fetchCourseData = async (
  timestamp: string | null,
  pageNumber = 1,
): Promise<boolean> => {
  const response = await apiService.get<IGetProfileBasedCoursesResponse>(
    `/api/v2/sync-courses/?page_size=${PAGE_SIZE}&page=${pageNumber}&c=${Date.now()}&${
      timestamp ? `last_pulled_at=${timestamp}` : ''
    }`,
  );
  const profileIds = Object.keys(response.data?.profiles ?? {});

  const profileData = getDataAgainstProfileIds(response.data);
  let lastResult = true;
  const models = [
    'coursesV2',
    'trainingsV2',
    'grandQuizzes',
    'levels',
  ] as const;
  // Process each profile
  for (const data of profileData) {
    // Map API response keys (courses/trainings) to V2 Dexie table names
    const dataWithV2Keys = {
      ...data,
      coursesV2: data.courses,
      trainingsV2: data.trainings,
    };
    try {
      const addDataToDb = await extractSyncResponse(
        models,
        dataWithV2Keys,
        teacherTrainingDB,
      );

      const courseData = [
        ...data.courses.created.data,
        ...data.courses.updated.data,
      ];

      const uniqueUrls = [
        ...new Set(courseData.map((item) => item.thumbnailUrl)),
      ];

      bulkImageDownloadFromUrlToBase64(uniqueUrls).then((promises) => {
        const matchingCourses: ICourse[] = [];
        promises.forEach((promise) => {
          let url = '';
          if ('value' in promise) {
            url = promise.value.url;
          }
          if ('reason' in promise) {
            url = promise.reason.message;
          }
          let urlToImageData = courseData.filter(
            (item) => item.thumbnailUrl === url,
          );
          if (promise.status === 'fulfilled') {
            urlToImageData = urlToImageData.map((item) => ({
              ...item,
              thumbnailBase64: promise.value.base64,
            }));
          } else {
            urlToImageData = urlToImageData.map((item) => ({
              ...item,
              thumbnailUrlDownloadAttempts: 1,
            }));
          }
          matchingCourses.push(...urlToImageData);
        });
        if (matchingCourses.length) {
          teacherTrainingDB.coursesV2.bulkPut(matchingCourses);
        }
      });

      await addTrainingStatusInDb([
        ...data.trainings.created.data,
        ...data.trainings.updated.data,
      ]);
      // Re-apply locally completed training statuses back onto trainingsV2.
      // extractSyncResponse (bulkPut) may have reset trainingStatus to null for
      // vendors not yet tracked by the backend (e.g. Oxbridge FE-only phase).
      await reapplyLocalTrainingStatuses(data.profileId);

      // Refresh denormalized course.level snapshots from the freshly-synced levels table.
      // Delta sync only returns courses whose modified timestamp changed — if a Level's
      // vendor field was added/changed but the Course itself was not modified, the course
      // stays in Dexie with a stale level snapshot (no vendor). Reading fresh level records
      // and patching coursesV2 ensures vendor (and other level fields) are always current.
      const profileId = data.profileId;
      const freshLevels = await teacherTrainingDB.levels
        .where('profileId')
        .equals(profileId)
        .toArray();
      if (freshLevels.length > 0) {
        const levelMap = new Map<number, ILevels>(
          freshLevels.map((level) => [level.id, level]),
        );
        await teacherTrainingDB.coursesV2
          .where('profileId')
          .equals(profileId)
          .modify((course: ICourse) => {
            if (course.level == null) return;
            const levelId = course.level.value;
            if (levelId == null) return;
            const freshLevel = levelMap.get(levelId);
            if (freshLevel == null) return;
            course.level = {
              ...course.level,
              vendor: freshLevel.vendor,
              order: freshLevel.order,
            };
          });
      }
    } catch (e) {
      lastResult = false;
      console.error(`Unable to add data for ${models} ${e}`);
    }
  }

  // Calculate pagination based on all profiles
  const allModelCounts = profileData.flatMap((data) =>
    getModelCount(models, {
      ...data,
      coursesV2: data.courses,
      trainingsV2: data.trainings,
    }),
  );
  const maxPages = Math.max(...allModelCounts) / PAGE_SIZE;

  if (pageNumber <= maxPages) {
    lastResult =
      lastResult && (await fetchCourseData(timestamp, pageNumber + 1));
  }

  return lastResult;
};
// Module-level guard: tracks which participant UUIDs have already been through
// the legacy attribution migration this session. Prevents re-scanning on every
// page of the recursive fetchCourseData loop.
const migratedParticipants = new Set<string>();

/** @visibleForTesting */
export function _resetMigratedParticipantsForTesting(): void {
  migratedParticipants.clear();
}

/**
 * One-time-per-session legacy attribution migration for trainingStatusV2 rows
 * with participant=undefined.
 *
 * A row's attribution is safe if and only if exactly ONE profile on this device
 * owns a trainingsV2 row for the same (training, course) pair — where the legacy
 * row's `training` maps to trainingsV2.id and the legacy row's `courseId` maps
 * to trainingsV2.course. If two profiles share the same (training, course) pair,
 * the legacy row is ambiguous and must remain unattributed.
 *
 * Performance: runs at most once per participant UUID per app session. The
 * module-level `migratedParticipants` Set prevents re-scanning on every page of
 * the recursive fetchCourseData loop.
 */
export async function migrateUnattributedTrainingStatuses(
  normalizedParticipant: string,
): Promise<void> {
  if (migratedParticipants.has(normalizedParticipant)) return;
  migratedParticipants.add(normalizedParticipant);

  const legacyStatusRows = await teacherTrainingDB.trainingStatusV2
    .filter((r) => r.status != null && !r.participant)
    .toArray();

  if (legacyStatusRows.length === 0) return;

  // Load all trainingsV2 rows once — cheaper than N per-row filter calls.
  const allTrainings = await teacherTrainingDB.trainingsV2.toArray();

  for (const legacy of legacyStatusRows) {
    // Match on BOTH dimensions: training id AND course id.
    // legacy.courseId maps to trainingsV2.course (different field names).
    const owners = allTrainings.filter(
      (r) => r.id === legacy.training && r.course === legacy.courseId,
    );
    const ownerProfiles = [
      ...new Set(
        owners
          .map((r) => (r.profileId ?? '').replace(/_/g, '-').toLowerCase())
          .filter(Boolean),
      ),
    ];

    if (ownerProfiles.length === 1 && ownerProfiles[0] === normalizedParticipant) {
      // Unambiguous: only this profile owns this (training, course) pair.
      // Pass the whole record (not numeric PK) — matches both real Dexie
      // and the fake table's update(existingRecord, patch) signature.
      await teacherTrainingDB.trainingStatusV2.update(legacy, {
        participant: normalizedParticipant,
      });
    }
    // ownerProfiles.length === 0: training not synced for any profile (stale row) — skip.
    // ownerProfiles.length >= 2: shared (training, course) across profiles — skip.
  }
}

/**
 * After a sync fetch, bulkPut overwrites trainingsV2 with backend data that may
 * have trainingStatus: null for vendors not yet fully tracked server-side (e.g.
 * Oxbridge in the FE-only phase). This function reads the locally authoritative
 * trainingStatusV2 table and re-stamps trainingStatus on the affected trainingsV2
 * records so that locally completed trainings survive a re-login.
 */
export async function reapplyLocalTrainingStatuses(participantId?: string): Promise<void> {
  const normalizedParticipant = participantId
    ? participantId.replace(/_/g, '-').toLowerCase()
    : undefined;

  // Run the one-shot legacy attribution migration before filtering.
  // This stamps legacy (participant=undefined) rows with the current participant's
  // UUID when attribution is unambiguous. Guarded by a session-level Set so it
  // runs at most once per participant UUID per app launch regardless of how many
  // fetchCourseData pages recurse through this function.
  if (normalizedParticipant) {
    await migrateUnattributedTrainingStatuses(normalizedParticipant);
  }

  const localStatuses = await teacherTrainingDB.trainingStatusV2
    .filter((r) => {
      if (r.status == null) return false;
      // Include records with no participant field (legacy records) only when we
      // have no participantId to filter on. When we do have a participantId,
      // require an explicit match so one profile's statuses cannot bleed onto
      // another profile's trainings.
      if (!normalizedParticipant) return true;
      if (!r.participant) return false;
      return r.participant.replace(/_/g, '-').toLowerCase() === normalizedParticipant;
    })
    .toArray();

  // Group by training ID and keep the highest-priority status.
  // Priority: Completed > InProgress > Pending.
  // This prevents a stale InProgress record from overwriting a Completed status
  // when multiple trainingStatusV2 records exist for the same training.
  const statusPriority: Record<string, number> = {
    COMPLETED: 3,
    IN_PROGRESS: 2,
    PENDING: 1,
  };

  const bestStatusByTraining = new Map<number, ProgressStatus>();
  for (const statusRecord of localStatuses) {
    const trainingId = statusRecord.training;
    const existing = bestStatusByTraining.get(trainingId);
    const currentPriority =
      statusPriority[statusRecord.status ?? 'PENDING'] ?? 0;
    const existingPriority = statusPriority[existing ?? 'PENDING'] ?? 0;
    if (currentPriority > existingPriority) {
      bestStatusByTraining.set(
        trainingId,
        statusRecord.status as ProgressStatus,
      );
    }
  }

  for (const [trainingId, status] of bestStatusByTraining) {
    await teacherTrainingDB.trainingsV2
      .filter((r) => {
        if (r.id !== trainingId) return false;
        if (!normalizedParticipant) return true;
        return (r.profileId ?? '').replace(/_/g, '-').toLowerCase() === normalizedParticipant;
      })
      .modify({ trainingStatus: status });
  }
}

/** @visibleForTesting */
export async function addTrainingStatusInDb(trainingsData: ITraining[]) {
  const trainingsWithStatus = trainingsData.filter(
    (training) => training.trainingStatus,
  );

  // Deduplicate by (training.id, training.course) - keep COMPLETED if multiple exist
  const statusMap = new Map<string, ITraining>();
  const statusPriority: Record<string, number> = {
    COMPLETED: 3,
    IN_PROGRESS: 2,
    PENDING: 1,
  };

  for (const training of trainingsWithStatus) {
    const key = `${training.id}:${training.course}`;
    const existing = statusMap.get(key);
    if (!existing) {
      statusMap.set(key, training);
    } else {
      const existingPriority =
        statusPriority[existing.trainingStatus ?? 'PENDING'] ?? 0;
      const newPriority =
        statusPriority[training.trainingStatus ?? 'PENDING'] ?? 0;
      if (newPriority > existingPriority) {
        statusMap.set(key, training);
      }
    }
  }

  // For each deduplicated training, look up whether a record already exists in
  // the DB for the same (training, courseId) pair.
  // - Existing record, higher-priority incoming status → update() (Dexie uses
  //   the record's internal PK, so no new auto-increment row is minted).
  // - Existing record, equal or lower-priority → skip (never downgrade).
  // - No existing record → add() a fresh row.
  // This prevents the duplicate-row bug where every sync call with no explicit
  // `id` in bulkPut would mint a new auto-increment row for the same training.
  for (const training of statusMap.values()) {
    const newPriority =
      statusPriority[training.trainingStatus ?? 'PENDING'] ?? 0;

    const profileId = training.profileId ?? undefined;
    const existingRecord = await teacherTrainingDB.trainingStatusV2
      .filter(
        (r) =>
          r.training === training.id &&
          r.courseId === training.course &&
          // Scoped match ONLY. The previous `(r.participant == null && profileId != null)`
          // legacy fallback was removed: it claimed any unattributed row for whichever
          // profile synced first, misattributing legacy statuses across profiles on a
          // multi-profile device (BUG-01 / architecture Gap 2). Safe attribution of
          // participant=null legacy rows is now owned exclusively by
          // migrateUnattributedTrainingStatuses (bug2 #5230), which verifies sole
          // ownership before stamping and runs before this code in the next session.
          (r.participant ?? '') === (profileId ?? ''),
      )
      .first();

    if (existingRecord) {
      const existingPriority =
        statusPriority[existingRecord.status ?? 'PENDING'] ?? 0;
      if (newPriority > existingPriority) {
        await teacherTrainingDB.trainingStatusV2.update(existingRecord, {
          status: training.trainingStatus as ProgressStatus,
          syncStatus: SyncRecordStatus.Synced,
          participant: existingRecord.participant ?? training.profileId ?? undefined,
        });
      }
      // Existing record has equal or higher priority — skip.
    } else {
      await teacherTrainingDB.trainingStatusV2.add({
        training: training.id,
        courseId: training.course,
        status: training.trainingStatus as ProgressStatus,
        syncStatus: SyncRecordStatus.Synced,
        participant: training.profileId ?? undefined,
      });
    }
  }
}

export const fetchTrainingQuestions = async (
  trainingId: number,
  timestamp: string | null,
  pageNumber = 1,
): Promise<boolean> => {
  const response = await apiService.get<IGetQuestionsReponse>(
    `api/v1/sync-training-questions/${trainingId}/?page_size=${PAGE_SIZE}&page=${pageNumber}&c=${Date.now()}&${
      timestamp ? `last_pulled_at=${timestamp}` : ''
    }`,
  );

  const data = response.data;
  let lastResult = true;
  const models = ['questions'] as const;
  try {
    await extractSyncResponse(models as unknown as string[], data, teacherTrainingDB);
  } catch (e) {
    lastResult = false;
    console.error(`Unable to add data for ${models} ${e}`);
  }

  const modelCounts = getModelCount(models, data);
  const maxPages = Math.max(...modelCounts) / PAGE_SIZE;

  if (pageNumber <= maxPages) {
    // fetch the next page.
    lastResult =
      lastResult &&
      (await fetchTrainingQuestions(trainingId, timestamp, pageNumber + 1));
  }

  return lastResult;
};
export const getOpenEndedFeedback = async (
  questionId: number,
  answer: string,
): Promise<IOpenEndedFeedbackResponse | null> => {
  const response = await apiService.post('api/v1/generate-question-feedback/', {
    question_id: questionId,
    user_answer: answer,
  });
  if (response) {
    return response.data as IOpenEndedFeedbackResponse;
  }
  return null;
};
export const getGrandQuiz = async (
  id: number,
): Promise<ITrainingQuestion[] | []> => {
  const response = await apiService.get<IGetGrandQuizResponse>(
    `api/v1/sync-grand-quiz-questions/${id}/?page_size=100&page=1`,
  );
  const data = response.data.questions.created.data ?? [];
  if (data.length > 0) {
    await teacherTrainingDB.grandQuizzesQuestions.bulkPut(data);
    return data;
  }
  return [];
};

/**
 * Fetch the GrandQuiz PK for a given level ID from the backend.
 *
 * Dexie's `grandQuizzes` table may be empty for a level whose GrandQuiz was
 * seeded after the user's last pull-sync (e.g. newly seeded BH ELT quiz).
 * This call goes directly to the API so it always returns the authoritative ID
 * without requiring a full sync first.
 *
 * @param levelId - The Level DB primary key (course.level.value / URL :level param)
 * @returns The GrandQuiz PK, or null if no active quiz exists for that level.
 */
export const getGrandQuizIdByLevel = async (
  levelId: number,
  type: 'grand_quiz' | 'diagnostic' = 'grand_quiz',
): Promise<number | null> => {
  try {
    const response = await apiService.get<{ results?: IGrandQuizzes[]; id?: number }>(
      `api/v1/grand_quizzes/?level=${levelId}&type=${type}`,
    );
    const all: IGrandQuizzes[] = Array.isArray(response.data)
      ? (response.data as unknown as IGrandQuizzes[])
      : (response.data.results ?? []);
    // Filter by type and is_active — backend may return inactive records
    // (e.g. is_active=false diagnostic alongside active grand_quiz for same level)
    const match =
      all.find((q) => q.type === type && (q as any).isActive !== false) ??
      all.find((q) => q.type === type);
    return match?.id ?? null;
  } catch (err) {
    console.error('[getGrandQuizIdByLevel] failed for levelId', levelId, err);
    return null;
  }
};
